import React, { useState } from 'react';
import { Loader2, X } from 'lucide-react';
import type { NotaFiscal } from '../../../shared/types';
import { Textarea } from '../../../shared/components/Textarea';
import { useRejeitarNotaFiscal } from '../hooks';
import { apiErrorMessage, formatCompetencia } from '../constants';
import { NFModalShell, btnSecondary } from './NFModalShell';

interface Props {
  nota: NotaFiscal;
  onClose: () => void;
  onRejected: (message: string) => void;
}

/** Rejeição exige motivo; ele aparece para o colaborador na tela de NF. */
export const RejeitarNFModal: React.FC<Props> = ({ nota, onClose, onRejected }) => {
  const rejeitar = useRejeitarNotaFiscal();
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  async function handleRejeitar() {
    if (!reason.trim()) {
      setError('Informe o motivo da rejeição');
      return;
    }
    setError(null);
    try {
      await rejeitar.mutateAsync({ id: nota.id, reason: reason.trim() });
      onRejected(`NF ${nota.number ?? ''} rejeitada. O colaborador poderá reenviar.`);
      onClose();
    } catch (err) {
      setError(apiErrorMessage(err, 'Não foi possível rejeitar a nota fiscal.'));
    }
  }

  return (
    <NFModalShell
      title="Rejeitar nota fiscal"
      subtitle={`${nota.collaboratorName ?? 'Colaborador'} · ${formatCompetencia(nota.referenceMonth)} · NF ${nota.number ?? '—'}`}
      onClose={onClose}
      footer={
        <>
          <button type="button" onClick={onClose} className={btnSecondary} disabled={rejeitar.isPending}>
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleRejeitar}
            disabled={rejeitar.isPending}
            className="inline-flex items-center gap-1.5 rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
          >
            {rejeitar.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <X className="h-4 w-4" />}
            Rejeitar
          </button>
        </>
      }
    >
      <Textarea
        label="Motivo da rejeição *"
        value={reason}
        maxLength={1000}
        showCharCount
        rows={4}
        onChange={(e) => setReason(e.target.value)}
        error={error ?? undefined}
      />
    </NFModalShell>
  );
};
